import Button from "@cloudscape-design/components/button"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useMemo, useState } from "react"

import type { GrantTarget } from "@/services/grantsApi"
import GrantAccessModal from "@/components/Access/GrantAccessModal"
import GrantsTable from "@/components/Access/GrantsTable"
import { useCapabilitySetsList } from "@/hooks/queries/useCapabilitySets"
import { useGrantsList } from "@/hooks/queries/useGrants"

interface StudyAccessPanelProps {
  studyId: string
}

/**
 * Study Access tab: who can access the study and its DTAs, with grant and revoke for managers.
 */
export default function StudyAccessPanel({ studyId }: StudyAccessPanelProps) {
  const target: GrantTarget = useMemo(() => ({ type: "study", id: studyId }), [studyId])
  const { grants, canManage, isPending, error } = useGrantsList(target)
  const { sets, isPending: setsPending, error: setsError } = useCapabilitySetsList()
  const [granting, setGranting] = useState(false)

  const setsByName = useMemo(() => new Map(sets.map((s) => [s.name, s])), [sets])
  // A study accepts resource-scoped internal sets; external sets are granted per DTA.
  const grantable = useMemo(
    () => sets.filter((s) => s.scope === "resource" && s.audience === "internal"),
    [sets],
  )

  return (
    <SpaceBetween size="m">
      <GrantsTable
        target={target}
        grants={grants}
        loading={isPending || setsPending}
        error={error ?? setsError}
        setsByName={setsByName}
        title="Access"
        description="People with access to this study and its DTAs"
        canManage={canManage}
        actions={
          canManage && (
            <Button variant="primary" iconName="add-plus" onClick={() => setGranting(true)}>
              Grant access
            </Button>
          )
        }
        note="Study grants cascade to all DTAs under this study. A person with a study grant does not need a separate grant on each DTA."
        empty="No one has been granted access to this study yet."
      />
      {granting && (
        <GrantAccessModal
          target={target}
          header={`Grant access - ${studyId}`}
          submitLabel="Grant access"
          sets={grantable}
          onClose={() => setGranting(false)}
        />
      )}
    </SpaceBetween>
  )
}
